// ============================================
// FeedbackButton: floating in-app product feedback
// ============================================
// Bug reports, feature ideas and general feedback, filed from anywhere in the
// app. Submissions go to this app's own edge function, which relays them
// server-to-server to the central TicketCommand portal — see
// supabase/functions/server/portal-relay.ts. Stacked just above the global
// voice-command button in the bottom-right corner.

import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { MessageSquare, X, Loader2, Check, Bug, Sparkles, MessageCircle } from 'lucide-react';
import { submitFeedback, type FeedbackKind, type FeedbackSeverity } from '../data/api';
import { useAuth } from '../data/AuthContext';
import pkg from '../../../package.json';

// Single source of truth for the version stamped on feedback — bump
// package.json and every submission carries it.
const APP_VERSION: string = pkg.version || 'unknown';

type Phase = 'ready' | 'saving' | 'done';

const KIND_OPTIONS: { value: FeedbackKind; label: string; icon: typeof Bug; description: string }[] = [
  { value: 'bug',      label: 'Bug',      icon: Bug,           description: "Something's broken" },
  { value: 'feature',  label: 'Feature',  icon: Sparkles,      description: 'A new idea' },
  { value: 'feedback', label: 'Feedback', icon: MessageCircle, description: 'Anything else' },
];

const SEVERITY_OPTIONS: { value: FeedbackSeverity; label: string }[] = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
];

export function FeedbackButton() {
  const location = useLocation();
  const { currentUser, accessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('ready');
  const [kind, setKind] = useState<FeedbackKind>('feedback');
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setPhase('ready');
    setKind('feedback');
    setSeverity('medium');
    setTitle('');
    setBody('');
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const handleSubmit = async () => {
    const text = body.trim();
    if (!text) {
      setError('Tell us a little about it before sending.');
      return;
    }
    setPhase('saving');
    setError('');
    try {
      await submitFeedback(accessToken, {
        kind,
        severity,
        title: title.trim() || undefined,
        body: text,
        path: location.pathname + location.search,
        user_agent: navigator.userAgent,
        app_version: APP_VERSION,
        // Client-asserted identity; the server prefers the verified session
        // token above and only falls back to these, marked unverified.
        submitter_email: currentUser?.email,
        submitter_name: currentUser?.name,
      });
      setPhase('done');
      setTimeout(handleClose, 1400);
    } catch (err: any) {
      setError(err?.message || 'Failed to send. Please try again.');
      setPhase('ready');
    }
  };

  // Don't show on the login page or other unauthenticated surfaces.
  if (!currentUser) return null;

  return (
    <>
      {/* Sits directly above the voice-command button (w-14 at bottom-6
          right-6); right-7 keeps the two circles on a shared center line. */}
      <button
        onClick={() => { reset(); setOpen(true); }}
        className="fixed bottom-[5.75rem] right-7 z-40 w-12 h-12 rounded-full bg-white border border-gray-200 text-indigo-600 hover:text-indigo-500 hover:border-indigo-200 shadow-lg shadow-indigo-500/10 flex items-center justify-center transition-all active:scale-95"
        title="Send feedback"
        aria-label="Send feedback"
      >
        <MessageSquare className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
          <div
            className="absolute inset-0"
            onClick={handleClose}
            aria-hidden
          />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-900 text-sm font-semibold">Send feedback</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  Bug, idea, or anything else — goes straight to the team.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors -m-1 p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {phase === 'done' ? (
              <div className="flex items-center gap-2 py-6 justify-center text-emerald-600">
                <Check className="w-5 h-5" />
                <span className="text-sm">Thanks — we got it.</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {KIND_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const active = kind === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setKind(opt.value)}
                        className={`px-2 py-2.5 rounded-xl border text-xs transition-colors flex flex-col items-center gap-1 ${
                          active
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-[0.65rem] leading-tight opacity-70">{opt.description}</span>
                      </button>
                    );
                  })}
                </div>

                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Title (optional)"
                  maxLength={200}
                  className="w-full mb-2 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />

                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder={
                    kind === 'bug'
                      ? 'What were you trying to do, and what happened instead?'
                      : kind === 'feature'
                      ? 'What would help? What problem does it solve?'
                      : 'Share anything on your mind — what works, what doesn’t, what’s confusing.'
                  }
                  rows={4}
                  maxLength={5000}
                  className="w-full mb-3 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
                />

                {kind === 'bug' && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1.5">How bad is it?</p>
                    <div className="flex gap-2">
                      {SEVERITY_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setSeverity(opt.value)}
                          className={`flex-1 px-2 py-1.5 rounded-lg border text-xs transition-colors ${
                            severity === opt.value
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 bg-gray-50 text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

                <p className="text-gray-400 text-[0.7rem] mb-3">
                  We&rsquo;ll attach the current page ({location.pathname}) and your account so we can follow up.
                </p>

                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 rounded-lg text-gray-500 hover:text-gray-700 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={phase === 'saving' || !body.trim()}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
                  >
                    {phase === 'saving' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending</>
                    ) : (
                      'Send'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
